import { useLazyQuery } from '@apollo/client/react'
import { useMemo } from 'react'

import { navSearchAutocompleteQuery } from '../../app/_components/chrome/PrimaryNavSearch.client'
import { derivePricingForVariantGroup } from '../../app/_components/pricing/PricingRange.client'
import { isDefinedAndNotNull } from '../../app/_utils/isDefinedAndNotNull'
import { SearchAutocompleteProduct } from '../../types/graphql/search'

export function useSearchAutocomplete() {
  const [fetchSearchAutocompleteResults, { data, previousData, error, loading }] = useLazyQuery(
    navSearchAutocompleteQuery,
    { notifyOnNetworkStatusChange: true }
  )

  const products: SearchAutocompleteProduct[] = useMemo(() => {
    return (data?.search.items.nodes ?? [])
      .map(node => node.productInfo.group)
      .filter(isDefinedAndNotNull)
      .map(variantGroup => {
        return {
          id: variantGroup.id,
          soldOut: variantGroup.availability === 'OUT_OF_STOCK',
          categoryId: variantGroup.primaryCategory.id,
          colorKey: variantGroup.color?.id,
          handle: variantGroup.product.handle,
          defaultShopifyProductId: variantGroup.defaultShopifyProduct.id,
          defaultShopifyVariantSku: variantGroup.defaultShopifyProduct.defaultShopifyVariant.sku,
          defaultShopifyVariantId: variantGroup.defaultShopifyProduct.defaultShopifyVariant.id,
          title: variantGroup.seoTitle,
          url: variantGroup.urlPath,
          imageSrc: variantGroup.images?.[0]?.url,
          pricing: derivePricingForVariantGroup(variantGroup),
          isGiftCard: variantGroup.primaryCategory.key === 'GIFT_CARD',
        }
      })
  }, [data?.search.items.nodes])

  const suggestions: string[] = useMemo(() => {
    return (
      data?.search.suggestions.map(suggestion => suggestion.query) ??
      previousData?.search.suggestions.map(suggestion => suggestion.query) ??
      []
    )
  }, [data, previousData])

  const queryRan: boolean = !!data || !!error
  const noResult: boolean = !loading && products.length === 0 && queryRan

  return { fetchSearchAutocompleteResults, products, suggestions, error, noResult, loading }
}
